import React, { useState, useEffect } from "react";
import { Lock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";

const SESSION_KEY = 'vision_token';
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

export const PasswordGate = ({ children }) => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileKey, setTurnstileKey] = useState(0);
  const [error, setError] = useState('');

  // Check session on mount
  useEffect(() => {
    const token = sessionStorage.getItem(SESSION_KEY);
    if (token) setAuthenticated(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch('/api/vision/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          password,
          turnstile_token: turnstileToken
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        sessionStorage.setItem(SESSION_KEY, data.token);
        setAuthenticated(true);
        toast.success("Welcome to VISION");
      } else {
        // Handle specific error codes
        if (data.code === 'BRUTE_FORCE_LOCKOUT') {
          setError('Too many attempts. Please wait 15 minutes.');
        } else if (data.code === 'TURNSTILE_FAILED') {
          setError('Bot verification failed. Please try again.');
        } else {
          setError(data.error || 'Authentication failed');
        }
        toast.error(data.error || "Authentication failed");
        setPassword('');
        setTurnstileToken(null);
        setTurnstileKey(prev => prev + 1); // Reset Turnstile
      }
    } catch (err) {
      console.error('[VISION AUTH] Error:', err);
      setError('Connection error. Please try again.');
      toast.error("Connection error");
    } finally {
      setIsLoading(false);
    }
  };

  // Render children if authenticated
  if (authenticated) return <>{children}</>;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 'var(--ds-space-lg, 24px)',
      backgroundColor: 'var(--ds-surface-muted, hsl(220 14% 96%))'
    }}>
      <div className="ds-card ds-card--elevated" style={{ 
        maxWidth: '380px', 
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ padding: '32px 24px', textAlign: 'center' }}>
          {/* Icon Circle */}
          <div style={{
            width: '64px',
            height: '64px',
            margin: '0 auto 16px',
            borderRadius: '50%',
            backgroundColor: 'hsl(221 83% 96%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Lock style={{ 
              width: '28px', 
              height: '28px', 
              color: 'hsl(221 83% 53%)' 
            }} />
          </div>
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700',
            color: 'hsl(220 15% 15%)',
            margin: '0 0 8px 0'
          }}>
            VISION Access
          </h2>
          <p style={{
            fontSize: '14px',
            color: 'hsl(220 9% 46%)',
            margin: '0 0 24px 0'
          }}>
            Enter password to continue
          </p>
          
          <form onSubmit={handleSubmit}>
            <input
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ 
                width: '100%',
                padding: '12px 16px',
                fontSize: '16px',
                textAlign: 'center',
                border: '1px solid hsl(220 13% 91%)',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
                marginBottom: '16px'
              }}
              autoFocus
            />
            
            {/* Error message display */}
            {error && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px',
                marginBottom: '16px',
                backgroundColor: 'hsl(0 84% 96%)',
                borderRadius: '8px',
                color: 'hsl(0 72% 51%)',
                fontSize: '14px'
              }}>
                <AlertTriangle size={16} />
                {error}
              </div>
            )}
            
            {/* Turnstile Bot Defense (if configured) */}
            {TURNSTILE_SITE_KEY && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                marginBottom: '16px' 
              }}>
                <Turnstile
                  key={turnstileKey}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => {
                    setError('Bot verification failed');
                    toast.error("Bot verification failed");
                  }}
                  onExpire={() => {
                    setTurnstileToken(null);
                    setTurnstileKey(prev => prev + 1);
                  }}
                />
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={!password || isLoading || (TURNSTILE_SITE_KEY && !turnstileToken)}
              style={{
                width: '100%',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: (!password || isLoading || (TURNSTILE_SITE_KEY && !turnstileToken)) 
                  ? 'hsl(220 14% 80%)' 
                  : 'hsl(221 83% 53%)',
                border: 'none',
                borderRadius: '8px',
                cursor: (!password || isLoading || (TURNSTILE_SITE_KEY && !turnstileToken)) 
                  ? 'not-allowed' 
                  : 'pointer',
                transition: 'background-color 200ms'
              }}
            >
              {isLoading ? "Verifying..." : "Enter VISION"}
            </button>
          </form>
          
          <p style={{
            fontSize: '12px',
            color: 'hsl(220 9% 46%)',
            marginTop: '24px'
          }}>
            ⚡ Jarvis Sovereign System
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordGate;
